import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {User} from '../../services/api.service';

export interface ActiveDelegationContext {
  actorUserId?: string | null;
  ownerUserId: string;
  ownerUsername?: string | null;
  permissions: string[];
  supportSessionId?: string | null;
  supportReason?: string | null;
  supportExpiresAt?: string | null;
  supportExpiresAtEpochMs?: number | null;
  supportMode?: 'delegation' | 'support' | 'admin_preview' | null;
}

const ACTIVE_DELEGATION_STORAGE_KEY = 'freyr-active-delegation';
const DELEGATED_ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/dashboard/commands': ['BOT_COMMANDS_READ', 'BOT_COMMANDS_WRITE'],
  '/dashboard/commands/custom': ['BOT_COMMANDS_READ', 'BOT_COMMANDS_WRITE'],
  '/dashboard/commands/default': ['BOT_COMMANDS_READ', 'BOT_COMMANDS_WRITE'],
  '/dashboard/integrations/imports': ['BOT_COMMANDS_READ', 'BOT_COMMANDS_WRITE', 'AUTO_MESSAGES_READ', 'AUTO_MESSAGES_WRITE'],
  '/dashboard/auto-messages': ['AUTO_MESSAGES_READ', 'AUTO_MESSAGES_WRITE'],
  '/dashboard/event-messages': ['AUTO_MESSAGES_READ', 'AUTO_MESSAGES_WRITE'],
  '/dashboard/moderation': ['MODERATION_READ', 'MODERATION_WRITE'],
	'/dashboard/planning': ['TWITCH_DATA_READ', 'TWITCH_SCHEDULE_WRITE'],
	'/dashboard/tools': ['LEVELS_READ', 'LEVELS_WRITE'],
	'/dashboard/tools/level': ['LEVELS_READ', 'LEVELS_WRITE'],
	'/dashboard/tools/level/followers': ['TWITCH_DATA_READ'],
	'/dashboard/tools/panel-streamer': ['LEVELS_READ', 'LEVELS_WRITE']
};
const DELEGATED_ROUTE_ORDER = [
  '/dashboard/commands',
  '/dashboard/auto-messages',
  '/dashboard/event-messages',
  '/dashboard/moderation',
	'/dashboard/tools',
  '/dashboard/planning',
  '/dashboard/access'
];

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly currentUser$ = new BehaviorSubject<User | null>(null);
  private readonly activeDelegation$ = new BehaviorSubject<ActiveDelegationContext | null>(this.readStoredDelegation());

  /** État de l'utilisateur connecté (null = non connecté ou inconnu). */
  get user(): User | null {
    return this.currentUser$.getValue();
  }

  /** Observable de l'utilisateur courant. */
  get user$(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  get activeDelegation(): ActiveDelegationContext | null {
    return this.activeDelegation$.getValue();
  }

  get activeDelegationChanges$(): Observable<ActiveDelegationContext | null> {
    return this.activeDelegation$.asObservable();
  }

  get hasActiveDelegation(): boolean {
    return this.activeDelegation != null;
  }

  get isSupportMode(): boolean {
    return this.activeDelegation?.supportMode === 'support';
  }

  get isAdminDashboardPreview(): boolean {
    return this.activeDelegation?.supportMode === 'admin_preview';
  }

  get delegationHomeRoute(): string {
    if (!this.hasActiveDelegation) {
      return '/dashboard';
    }
    if (this.isAdminDashboardPreview) {
      return '/dashboard';
    }
    return DELEGATED_ROUTE_ORDER.find(route => this.canAccessDelegatedRoute(route)) ?? '/dashboard/access';
  }

  /** Indique si quelqu'un est connecté. */
  get isLoggedIn(): boolean {
    return this.currentUser$.getValue() != null;
  }

  setUser(user: User | null): void {
    if (user === null) {
      this.clearActiveDelegation();
      this.currentUser$.next(null);
      return;
    }

    const activeDelegation = this.activeDelegation;
    // En prévisualisation admin, GET /me renvoie le profil de l'utilisateur cible (en-têtes délégation + preview) :
    // ne pas remplacer l'identité JWT (admin) dans le client, ni effacer la délégation (sinon perte de l'aperçu).
    if (
      activeDelegation?.supportMode === 'admin_preview'
      && activeDelegation.ownerUserId
      && user.id === activeDelegation.ownerUserId
    ) {
      return;
    }

    const nextUserId = user.id;
    if (activeDelegation && activeDelegation.supportMode !== 'admin_preview') {
      const normalizedActorUserId = (activeDelegation.actorUserId ?? '').trim();
      // Ne pas effacer si actorUserId est absent (ex. ancien localStorage) : l'identité réelle vient du JWT côté API.
      if (normalizedActorUserId && normalizedActorUserId !== nextUserId) {
        this.clearActiveDelegation();
      }
    }
    this.currentUser$.next(user);
  }

  clearUser(): void {
    this.clearActiveDelegation();
    this.currentUser$.next(null);
  }

  setActiveDelegation(context: ActiveDelegationContext | null): void {
    if (!context?.ownerUserId) {
      this.clearActiveDelegation();
      return;
    }
    const resolvedMode: ActiveDelegationContext['supportMode'] =
      context.supportMode === 'support'
        ? 'support'
        : context.supportMode === 'admin_preview'
          ? 'admin_preview'
          : 'delegation';
    const normalized: ActiveDelegationContext = {
      actorUserId:
        context.supportMode === 'support'
          ? (context.actorUserId ?? this.user?.id ?? null)
          : (this.user?.id ?? context.actorUserId ?? null),
      ownerUserId: context.ownerUserId,
      ownerUsername: context.ownerUsername ?? null,
      permissions: Array.isArray(context.permissions) ? [...context.permissions] : [],
      supportSessionId: context.supportSessionId ?? null,
      supportReason: context.supportReason ?? null,
      supportExpiresAt: context.supportExpiresAt ?? null,
      supportExpiresAtEpochMs: typeof context.supportExpiresAtEpochMs === 'number'
        ? context.supportExpiresAtEpochMs
        : null,
      supportMode: resolvedMode
    };
    this.activeDelegation$.next(normalized);
    this.persistDelegation(normalized);
  }

  clearActiveDelegation(): void {
    this.activeDelegation$.next(null);
    this.persistDelegation(null);
  }

  isDelegatingFor(ownerUserId: string | null | undefined): boolean {
    return !!ownerUserId && this.activeDelegation?.ownerUserId === ownerUserId;
  }

  canAccessDelegatedRoute(route: string | null | undefined): boolean {
    if (!this.hasActiveDelegation) {
      return true;
    }

    const normalizedRoute = (route ?? '').split('?')[0].trim().replace(/\/$/, '') || '/';
    if (this.activeDelegation?.supportMode === 'admin_preview') {
      return normalizedRoute === '/dashboard' || normalizedRoute.startsWith('/dashboard/');
    }
    if (!normalizedRoute || normalizedRoute === '/dashboard') {
      return false;
    }
    if (normalizedRoute === '/dashboard/access') {
      return true;
    }

    const requiredPermissions = DELEGATED_ROUTE_PERMISSIONS[normalizedRoute];
    if (!requiredPermissions?.length) {
      return false;
    }

    const grantedPermissions = new Set(
      (this.activeDelegation?.permissions ?? [])
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map(value => value.trim().toUpperCase())
    );
    return requiredPermissions.some(permission => grantedPermissions.has(permission));
  }

  hasDelegatedPermission(permission: string | null | undefined): boolean {
    if (!this.hasActiveDelegation) {
      return true;
    }
    const normalizedPermission = (permission ?? '').trim().toUpperCase();
    if (!normalizedPermission) {
      return false;
    }
    return (this.activeDelegation?.permissions ?? [])
      .some(value => typeof value === 'string' && value.trim().toUpperCase() === normalizedPermission);
  }

  private readStoredDelegation(): ActiveDelegationContext | null {
    try {
      // Delegation/support context is tab-scoped. localStorage caused cross-tab leaks:
      // a support session opened in one tab could redirect another tab to the wrong account.
      const raw = sessionStorage.getItem(ACTIVE_DELEGATION_STORAGE_KEY);
      // Clear the legacy shared storage key when present so stale support contexts stop leaking across tabs.
      localStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ActiveDelegationContext | null;
      if (!parsed?.ownerUserId) return null;
      // Ne jamais restaurer une prévisualisation admin depuis le stockage : l’URL (ou la garde) doit la poser.
      // Sinon un UUID erroné ou obsolète restait appliqué au bootstrap et cassait GET /me (en-têtes de délégation).
      if (parsed.supportMode === 'admin_preview') {
        sessionStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
        return null;
      }
      return {
        actorUserId: typeof parsed.actorUserId === 'string' && parsed.actorUserId.trim().length > 0
          ? parsed.actorUserId.trim()
          : null,
        ownerUserId: parsed.ownerUserId,
        ownerUsername: parsed.ownerUsername ?? null,
        permissions: Array.isArray(parsed.permissions) ? [...parsed.permissions] : [],
        supportSessionId: typeof parsed.supportSessionId === 'string' && parsed.supportSessionId.trim().length > 0
          ? parsed.supportSessionId.trim()
          : null,
        supportReason: typeof parsed.supportReason === 'string' && parsed.supportReason.trim().length > 0
          ? parsed.supportReason.trim()
          : null,
        supportExpiresAt: typeof parsed.supportExpiresAt === 'string' && parsed.supportExpiresAt.trim().length > 0
          ? parsed.supportExpiresAt.trim()
          : null,
        supportExpiresAtEpochMs: typeof parsed.supportExpiresAtEpochMs === 'number'
          ? parsed.supportExpiresAtEpochMs
          : null,
        supportMode: parsed.supportMode === 'support' ? 'support' : 'delegation'
      };
    } catch {
      return null;
    }
  }

  private persistDelegation(context: ActiveDelegationContext | null): void {
    try {
      if (!context) {
        sessionStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
        return;
      }
      if (context.supportMode === 'admin_preview') {
        sessionStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(ACTIVE_DELEGATION_STORAGE_KEY, JSON.stringify(context));
      localStorage.removeItem(ACTIVE_DELEGATION_STORAGE_KEY);
    } catch {
      // ignore storage failures
    }
  }
}

